import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PRICING_METHODOLOGY_MAP, COMMISSION_TYPE_MAP, CommissionType } from '@constants/constants';
import { ActiveCohortItem } from '@api/types/commissions';
import { RootState } from '@stores/store';
import DrawerComponent from '../../../../../components/Drawer/Drawer';
import CustomButton from '../../../../../components/CustomButton/CustomButton';
import ActiveCohortModal from './ActiveCohortModal';
import { formatPercentage, formatCurrency, getPricingDetails } from '../utils';
import LabeledValue, { LabeledValueProp } from '../components/LabeledValue';

interface ActiveCohortSidePanelProps {
  showActiveCohortSidePanel: boolean;
  setShowActiveCohortSidePanel: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRowData: ActiveCohortItem;
  refresh: () => void;
}

const ActiveCohortSidePanel: React.FC<ActiveCohortSidePanelProps> = ({
  showActiveCohortSidePanel,
  setShowActiveCohortSidePanel,
  selectedRowData,
  refresh,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showActiveCohortModal, setShowActiveCohortModal] = React.useState(false);

  const userRole = useSelector((state: RootState) => state.UserData.user.role);

  const infoData: LabeledValueProp[] = [
    { label: 'Referrer', value: selectedRowData.referrer },
    { label: '', value: '' },
    { label: 'Deal Name', value: selectedRowData.dealName },
    { label: 'Funding Month', value: selectedRowData.fundingMonth },
    {
      label: 'Gross Funding Amount (USD)',
      value: selectedRowData.grossFundingAmount,
      formatValue: formatCurrency,
    },
    {
      label: 'Collection (USD)',
      value: selectedRowData.grossRecoupAmount,
      formatValue: formatCurrency,
    },
    {
      label: 'Actual IRR ',
      value: selectedRowData.irr,
      formatValue: formatPercentage,
    },
    {
      label: 'MOIC',
      value: selectedRowData.moic,
      formatValue: formatPercentage,
    },
    {
      label: 'Pricing Methodology',
      value: PRICING_METHODOLOGY_MAP[selectedRowData.pricingMethodology],
    },
    ...getPricingDetails(selectedRowData.pricingMethodology, selectedRowData.interest),
    {
      label: 'Hurdle Rate',
      value: selectedRowData.hurdleRate,
      formatValue: formatPercentage,
    },
    {
      label: 'Commission %',
      value: `${formatPercentage(selectedRowData.commissionPercentage)} • ${selectedRowData.commissionType === CommissionType.COHORT_BASED ? `${selectedRowData.numberOfCohorts} Cohort` : COMMISSION_TYPE_MAP[selectedRowData.commissionType]}`,
    },
    { label: 'Status', value: 'Active' },
  ];

  return (
    <>
      <DrawerComponent
        open={showActiveCohortSidePanel}
        anchor="right"
        close={() => {
          setShowActiveCohortSidePanel(false);
        }}
        height="98vh"
        zIndex={9999}
        backgroundColor="#F0F0F1"
      >
        <Box
          sx={{
            padding: '1.5rem',
            width: isMobile ? 'auto' : '600px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '1.375rem',
              fontWeight: 700,
              lineHeight: '1.875rem',
              color: '#6C6C6C',
            }}
          >
            Active Cohort
            <Button
              sx={{ padding: 0, minWidth: 'fit-content' }}
              onClick={() => {
                setShowActiveCohortSidePanel(false);
              }}
            >
              <CloseIcon sx={{ color: '#162C36' }} />
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            margin: '1rem 1.5rem',
            width: isMobile ? 'auto' : '552px',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          <Box
            sx={{
              padding: '1rem',
              borderRadius: '1rem',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              {infoData.map(({ label, value, formatValue }) => (
                <LabeledValue key={label} label={label} value={value} formatValue={formatValue} />
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              padding: '1rem',
              borderRadius: '1rem',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                color: '#162936',
                lineHeight: '1.25rem',
              }}
            >
              Final Commission Amount
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#6C6C6C',
                lineHeight: '1.25rem',
              }}
            >
              Commission ($) = Max(0,(Gross Funding Amount * (MOIC-1) * ((Actual IRR-Hurdle
              Rate)/Actual IRR) * Commissions rate))
            </Typography>
            <Box
              sx={{
                backgroundColor: '#2F736E14',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: '#162936',
                  lineHeight: '1.25rem',
                  alignItems: 'center',
                  display: 'flex',
                  gap: '0.25rem',
                }}
              >
                Commissions will be calculated after the cohort is marked as closed
              </Typography>
            </Box>
          </Box>
          {userRole === 'ADMIN' && (
            <CustomButton
              color={''}
              borderColor="#2E4A4F"
              name={'Mark as Defaulted'}
              onClick={() => setShowActiveCohortModal(true)}
              width="fit-content"
            />
          )}
        </Box>
      </DrawerComponent>
      <ActiveCohortModal
        id={selectedRowData.cohortDetailsId}
        open={showActiveCohortModal}
        close={setShowActiveCohortModal}
        setShowActiveCohortSidePanel={setShowActiveCohortSidePanel}
        refresh={refresh}
      />
    </>
  );
};

export default ActiveCohortSidePanel;
